import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import PRZImageT from '@Components/Common/UI/PRZImageT';
import PRZTextT from '@Components/Common/UI/PRZTextT';
import UserActions from '../../../../actions/userActions';
import Helpers from '@Apis/helpers';
import documentUINamesConstant from '../../../../apis/documentUINamesConstant';
import confetti from '@Images/icons/confetti.png';
import PendingSkeleton from './PendingSkeleton';
import './style.scss';
const PendingActions = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.UserReducers.user);
    const getPendingActionsLoading = useSelector((state) => state.UserReducers.getPendingActionsLoading);
    const pendingActions = useSelector((state) => state.UserReducers.pendingActions);
    const [documentUINames, setDocumentUINames] = useState({});
    useEffect(() => {
        if (user) {
            setDocumentUINames(documentUINamesConstant(user));
            dispatch(UserActions.getPendingActions(null, null, null, () => { }));
        }
    }, [user, dispatch]);
    const selectedApproverIsEnable = user?.tenant_info?.quality_control_config?.sub_modules?.quality_check?.settings?.only_assignees_can_approve_quality_check;
    const actionConfig = useMemo(() => ([
        {
            count: pendingActions?.pr_approval_count,
            permission: Helpers.permissionEntities.PURCHASE_REQUEST,
            link: '/actions/pending-purchase-requests',
            label: 'Material Requests',
            bg: '#E9F7FF',
            border: '#c1e8ff',
        },
        {
            count: pendingActions?.pi_approval_count,
            permission: Helpers.permissionEntities.PURCHASE_INDENT,
            link: '/actions/pending-purchase-indents',
            label: 'Purchase Indents',
            bg: '#E9F7FF',
            border: '#c1e8ff',
        },
        {
            count: pendingActions?.po_approval_count,
            permission: Helpers.permissionEntities.PURCHASE_ORDER,
            link: '/actions/pending-purchase-orders',
            label: 'Purchase Orders',
            bg: '#E9F7FF',
            border: '#c1e8ff',
        },
        {
            count: pendingActions?.grn_approval_count,
            permission: Helpers.permissionEntities.GOOD_RECEIVING,
            link: '/actions/pending-good-receiving',
            label: 'Goods Receiving Notes',
            bg: '#F0E7FF',
            border: '#decfff',
        },
        {
            count: pendingActions?.api_approval_count,
            permission: Helpers.permissionEntities.ACCOUNT_PAYABLE_INVOICE,
            link: '/actions/pending-account-payable-invoices',
            label: 'Account Payable Invoice',
            bg: '#F0E7FF',
            border: '#decfff',
        },
        {
            count: pendingActions?.payment_request_approval_count,
            permission: Helpers.permissionEntities.VENDOR_PAYOUT,
            link: '/actions/pending-payment-approvals',
            label: 'Payment Requests',
            bg: '#F0E7FF',
            border: '#decfff',
        },
        {
            count: pendingActions?.se_approval_count,
            permission: Helpers.permissionEntities.SALES_ESTIMATE,
            link: '/actions/pending-estimates',
            label: `${documentUINames.estimateUIName}s`,
            bg: '#FFE6BF',
            border: '#ffdeac',
        },
        {
            count: pendingActions?.so_approval_count,
            permission: Helpers.permissionEntities.SALES_ORDER,
            link: '/actions/pending-sales-orders',
            label: `${documentUINames?.salesOrderUIName || 'Sales Order'}s`,
            bg: '#FFE6BF',
            border: '#ffdeac',
        },
        {
            count: pendingActions?.inv_approval_count,
            permission: Helpers.permissionEntities.INVOICE,
            link: '/actions/pending-invoices',
            label: 'Invoices',
            bg: '#FFE6BF',
            border: '#ffdeac',
        },
        {
            count: pendingActions?.sa_approval_count,
            permission: Helpers.permissionEntities.INVENTORY_INDENT,
            link: '/actions/pending-stock-adjustment',
            label: 'Stock Adjustments',
            bg: '#cefad0',
            border: '#abf7b1',
        },
        {
            count: pendingActions?.st_approval_count,
            permission: Helpers.permissionEntities.STOCK_TRANSFER,
            link: '/actions/pending-stock-transfer',
            label: 'Stock Transfers',
            bg: '#cefad0',
            border: '#abf7b1',
        },
        {
            count: pendingActions?.pending_qc_count,
            permission: Helpers.permissionEntities.QUALITY_CHECKS,
            link: `/quality/quality-checks?page=1&limit=10&status=PENDING&tenant=${Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.CREATE)?.join(',')}&onlyApprover=${selectedApproverIsEnable}`,
            label: 'Quality Checks',
            bg: '#cefad0',
            border: '#abf7b1',
            check: () => Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.CREATE)?.length > 0,
        },
    ]), [pendingActions, user, documentUINames, selectedApproverIsEnable]);
    const hasActions = useMemo(() => actionConfig.some((item) => Number(item.count) > 0 &&
        (item.check ? item.check() : Helpers.getPermission(item.permission, Helpers.permissionTypes.READ, user))), [actionConfig, user]);
    if (!hasActions && !getPendingActionsLoading) {
        return (_jsxs("div", { className: "pending-actions__loader", children: [_jsx(PRZImageT, { src: confetti, loading: "lazy", className: "pending-actions__loader-image" }), _jsx(PRZTextT, { text: "There are no pending actions right now", className: "pending-actions__loader-text" })] }));
    }
    return (_jsx("div", { className: "pending-actions__wrapper doc-list__wrapper doc-list__tab-content", children: getPendingActionsLoading ? (_jsx(PendingSkeleton, {})) : (_jsx("div", { className: "ant-row", children: actionConfig.map((item, idx) => {
                const hasPermission = item.check
                    ? item.check()
                    : Helpers.getPermission(item.permission, Helpers.permissionTypes.READ, user);
                if (!hasPermission || Number(item.count) <= 0)
                    return null;
                return (_jsx("div", { className: "ant-col-md-6 ant-col-xs-12", children: _jsx(Link, { to: item.link, children: _jsxs("div", { className: "pending-actions__item", style: { backgroundColor: item.bg, borderColor: item.border }, children: [_jsx(PRZTextT, { className: `pending-actions__item-stat ${getPendingActionsLoading ? 'lcp-skeleton' : ''}`, text: getPendingActionsLoading ? 'Loading...' : `${item.count} ${item.label}` }), _jsx(PRZTextT, { text: "Pending on your approval", className: "pending-actions__item-text" })] }) }) }, idx));
            }) })) }));
};
export default withRouter(PendingActions);
